import React from 'react';

import { TrackingContext, IMPRESSION_DURATION } from '@shopee-user/tracking';

const defaultIsEqualInitialState = (a: any, b: any) => {
  try {
    return Object.is(a, b);
  } catch {
    // in case Object.is is not supported
    return a === b;
  }
};

/**
 * @description
 * This hook is used to restore the state to the initial state after a timeout.
 * @param initialState - The initial state.
 * @param timeout - The timeout in milliseconds.
 * @returns The state and the setState function.
 */
export function useAutoRestoreState<T>(
  initialState: T,
  {
    timeout,
    isEqualInitialState = defaultIsEqualInitialState,
  }: {
    timeout: number;
    isEqualInitialState?: (a: T, b: T) => boolean;
  }
) {
  const initialStateRef = React.useRef<T>(initialState);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const [state, setState] = React.useState(initialState);

  React.useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (!isEqualInitialState(state, initialStateRef.current)) {
      timeoutRef.current = setTimeout(() => {
        setState(initialStateRef.current);
      }, timeout);
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [state, timeout]);

  return [state, setState] as const;
}

// Forked from @shopee-user/tracking/hooks/useTrackImpression
export function useFlowEventImpressionRef({
  onObserve,
}: {
  onObserve: () => void;
}) {
  const [trackingElem, setTrackingElem] = React.useState<
    HTMLElement | null | undefined
  >(null);
  const timeoutIdRef = React.useRef<number | null>(null);
  const { trackingObserver: observer } = React.useContext(TrackingContext);

  const onEnter = () => {
    timeoutIdRef.current = self.setTimeout(() => {
      onObserve();
      unobserveCurrentElement();
    }, IMPRESSION_DURATION);
  };

  const onLeave = () => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }
  };

  const unobserveCurrentElement = () => {
    if (observer && trackingElem) {
      observer.unobserve(trackingElem);
    }
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }
  };

  React.useEffect(() => {
    if (observer && trackingElem) {
      observer.observe(trackingElem, {
        onEnter,
        onLeave,
      });
    }

    return unobserveCurrentElement;
  }, [trackingElem]);

  const proxiedSetTrackingElem = React.useCallback(
    (el: HTMLElement | null | undefined) => {
      setTrackingElem(el);
    },
    [setTrackingElem]
  );

  return proxiedSetTrackingElem;
}
